import { platform, projectCatalogCategories, projectPhaseSearchType, refParamTypes } from '@/Models';
import { projectCatalogCategoryIcons, projectExplorationTagKeys } from '@/Utils/Views/Home/HomeUtilityConstants';
import {
    featuredProjectSearchParamsValues,
    projectExplorerSearchParamsValues,
} from '@/Utils/Views/Home/HomeUtilitySearchParams';
import {
    buildExploreUrlParams as buildSearchUrl,
    getHomeProjectListCategoryKeys,
} from '@/Utils/Views/Home/HomeUtilityHelpers';
import { camelCaseToKebabCase, formatStringToRefValue } from '@/Utils/StringUtility';
import { addRefWithOptions } from '@/Utils/UrlUtility';

const phaseName = phase => projectPhaseSearchType[phase].name;
const categoryName = category => projectCatalogCategories[category].name;
const categoryRefSuffix = category => camelCaseToKebabCase(getHomeProjectListCategoryKeys(category));

function buildTagItem(label, icon, tagKey) {
    return {
        label,
        icon,
        url: buildSearchUrl(projectExplorerSearchParamsValues[tagKey]),
        refSuffix: camelCaseToKebabCase(tagKey),
    };
}

function joinLabel(resources, first, second) {
    return resources.format(resources.Layout.SearchFormCombinedLabel, first, second);
}

function buildGamefoundSections(resources, popularTags) {
    const homepage = resources.Homepage;
    const categories = resources.ProjectCatalogCategories;

    return [
        {
            key: 'discover',
            title: resources.Layout.SearchFormDiscoverTitle,
            items: [
                buildTagItem(joinLabel(resources, homepage.ProjectListItemTypeUpcomingTitle, homepage.ProjectListComingSoonSegmentTitle), 'clock', projectExplorationTagKeys.upcomingComingSoon),
                buildTagItem(joinLabel(resources, homepage.ProjectListItemTypeUpcomingTitle, homepage.ProjectListNewSegmentTitle), 'sun', projectExplorationTagKeys.upcomingNew),
                buildTagItem(joinLabel(resources, homepage.ProjectListItemTypeCrowdfundingTitle, homepage.ProjectListTrendingSegmentTitle), 'chart-line', projectExplorationTagKeys.crowdfundingTrending),
                buildTagItem(joinLabel(resources, homepage.ProjectListItemTypeCrowdfundingTitle, homepage.ProjectListNewSegmentTitle), 'rocket', projectExplorationTagKeys.crowdfundingNew),
                buildTagItem(homepage.ExploreSegmentEndingSoon, 'hourglass', projectExplorationTagKeys.exploreEndingSoon),
                buildTagItem(joinLabel(resources, homepage.ProjectListItemTypeLatePledgeTitle, homepage.ProjectListNewSegmentTitle), 'shopping-bag', projectExplorationTagKeys.latePledgeNew),
            ],
        },
        {
            key: 'category',
            title: resources.Search.SearchProjectsFiltersCategoryLabel,
            items: [
                projectCatalogCategories.boardAndCardGames,
                projectCatalogCategories.ttrpg,
                projectCatalogCategories.videoGames,
                projectCatalogCategories.accessories,
                projectCatalogCategories.others,
            ].map(category => ({
                label: categories[categoryName(category)],
                icon: projectCatalogCategoryIcons[category],
                url: buildSearchUrl({ projectCatalogCategories: categoryName(category) }),
                refSuffix: categoryRefSuffix(category),
            })),
        },
        {
            key: 'popular-tags',
            title: resources.Layout.SearchFormPopularTagsTitle,
            items: popularTags.map(tag => ({
                label: tag.name,
                url: tag.url,
                refSuffix: formatStringToRefValue(tag.name),
            })),
        },
    ];
}

function buildIdeafoundSections(resources) {
    const homepage = resources.Homepage;
    const categories = resources.ProjectCatalogCategories;
    const crowdfunding = homepage.ProjectListItemTypeCrowdfundingTitle;
    const upcoming = homepage.ProjectListItemTypeUpcomingTitle;

    return [
        {
            key: 'discover',
            title: resources.Layout.SearchFormDiscoverTitle,
            items: [
                { label: resources.Layout.SearchFormAllProjectsLabel, icon: 'chart-line', url: buildSearchUrl({}), refSuffix: 'all-projects' },
                { label: joinLabel(resources, crowdfunding, categories.VideoGames), icon: 'shopping-bag', url: buildSearchUrl({ projectCatalogCategories: categoryName(projectCatalogCategories.videoGames), projectPhaseSearchTypes: phaseName(projectPhaseSearchType.crowdfunding), source: 'Filtered' }), refSuffix: 'crowdfunding-video-games' },
                buildTagItem(joinLabel(resources, crowdfunding, categories.TechAndInnovation), 'rocket', projectExplorationTagKeys.techAndInnovationCrowdfunding),
                buildTagItem(crowdfunding, 'clock', projectExplorationTagKeys.crowdfundingTrending),
                buildTagItem(joinLabel(resources, upcoming, categories.TechAndInnovation), 'sun', projectExplorationTagKeys.techAndInnovationUpcoming),
            ],
        },
        {
            key: 'category',
            title: resources.Search.SearchProjectsFiltersCategoryLabel,
            items: [
                [projectCatalogCategories.techAndInnovation, 'gear'],
                [projectCatalogCategories.creativeWorks, 'desktop'],
                [projectCatalogCategories.communityProjects, 'users'],
                [projectCatalogCategories.gamingCulture, 'gamepad-modern'],
            ].map(([category, icon]) => ({
                label: categories[categoryName(category)],
                icon,
                url: buildSearchUrl(featuredProjectSearchParamsValues[category]),
                refSuffix: categoryRefSuffix(category),
            })),
        },
        {
            key: 'popular-subcategories',
            title: resources.Layout.SearchFormPopularSubcategoriesTitle,
            items: [
                projectCatalogCategories.film,
                projectCatalogCategories.videoGames,
                projectCatalogCategories.home,
                projectCatalogCategories.comics,
                projectCatalogCategories.travelAndOutdoors,
                projectCatalogCategories.healthAndFitness,
                projectCatalogCategories.productivity,
                projectCatalogCategories.phonesAndAccessories,
                projectCatalogCategories.audio,
                projectCatalogCategories.education,
            ].map(category => ({
                label: categories[categoryName(category)],
                url: buildSearchUrl({ projectCatalogCategories: categoryName(category) }),
                refSuffix: categoryRefSuffix(category),
            })),
        },
    ];
}

// Every shortcut is tracked as search_<section key>_<item>_<position>, e.g. search_discover_upcoming-coming-soon_1.
function addSearchRefs(section) {
    return {
        ...section,
        items: section.items.map(({ refSuffix, ...item }, index) => ({
            ...item,
            url: addRefWithOptions(item.url, {
                page: refParamTypes.searchRef,
                section: section.key,
                suffix: refSuffix,
                index: index + 1,
            }),
        })),
    };
}

export function buildSearchDiscoverySections({ currentPlatform, resources, popularTags = [] }) {
    const sections = currentPlatform === platform.ideafound
        ? buildIdeafoundSections(resources)
        : buildGamefoundSections(resources, popularTags);

    return sections.map(addSearchRefs);
}
